import React, { useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import QRCodeDisplay from './QRCodeDisplay';
import { Link } from 'react-router-dom';
import WalletConnect from './WalletConnect';

const AirdropOrderPage = () => {
  const [formData, setFormData] = useState({
    addressCount: '',
    airdropAmount: '',
    addressType: 'random', // 默认是随机
    specifiedDesc: '',
    inscriptionName: '',
    orderAddress: '' // 用户的接收地址
  });

  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressInputMethod, setAddressInputMethod] = useState('wallet'); // 'wallet' 或 'manual'

  const handleWalletAddressReceived = (address) => {
    if (addressInputMethod === 'wallet') {
      setFormData(prev => ({
        ...prev,
        orderAddress: address
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInputMethodChange = (method) => {
    setAddressInputMethod(method);
    // 如果切换到手动输入，清空地址
    if (method === 'manual') {
      setFormData(prev => ({
        ...prev,
        orderAddress: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 表单验证
      if (!formData.addressCount || !formData.inscriptionName) {
        throw new Error('请填写所有必填字段');
      }

      if (isNaN(formData.addressCount) || parseInt(formData.addressCount) <= 0) {
        throw new Error('地址数量必须是正整数');
      }

      // 检查用户 BTC 地址是否已设置
      if (!formData.orderAddress) {
        throw new Error('请提供BTC地址');
      }

      // 验证BTC地址格式（简单验证）
      if (!/^(bc1|[13])[a-zA-HJ-NP-Z0-9]{25,62}$/.test(formData.orderAddress)) {
        throw new Error('请输入有效的BTC地址');
      }

      // 调用后端API
      // 注意：这里需要替换为实际的API端点
      const response = await axios.post('/backend/api/airdrop/order', formData);

      setPaymentInfo(response.data.data);
    } catch (err) {
      setError(err.message || '下单失败，请稍后重试');
      console.error('Order submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <PageTitle>BTC 铭文空投系统</PageTitle>

      {!paymentInfo ? (
        <>
          {addressInputMethod === 'wallet' && (
            <WalletConnect onAddressReceived={handleWalletAddressReceived} />
          )}

          <AddressMethodSelector>
            <MethodButton 
              active={addressInputMethod === 'wallet'} 
              onClick={() => handleInputMethodChange('wallet')}
            >
              使用OKX钱包
            </MethodButton>
            <MethodButton 
              active={addressInputMethod === 'manual'} 
              onClick={() => handleInputMethodChange('manual')}
            >
              手动输入地址
            </MethodButton>
          </AddressMethodSelector>

          <FormContainer onSubmit={handleSubmit}>
            <FormTitle>空投下单</FormTitle>

            <FormGroup>
              <Label htmlFor="inscriptionName">铭文名称</Label>
              <Input
                type="text"
                id="inscriptionName"
                name="inscriptionName"
                value={formData.inscriptionName}
                onChange={handleChange}
                placeholder="请输入铭文名称"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="addressCount">空投地址数量</Label>
              <Input
                type="number"
                id="addressCount"
                name="addressCount"
                value={formData.addressCount}
                onChange={handleChange}
                placeholder="请输入地址数量"
                min="1"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="airdropAmount">每个地址空投数量</Label>
              <Input
                type="number"
                id="airdropAmount"
                name="airdropAmount"
                value={formData.airdropAmount}
                onChange={handleChange}
                placeholder="请输入每个地址空投数量"
                min="1"
              />
            </FormGroup>

            <FormGroup>
              <Label>空投地址类别</Label>
              <RegionRestriction>服务费为: 随机地址 每100个 0.0015 BTC, 指定地址 每100个 0.002 BTC</RegionRestriction>
              <RadioGroup>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="addressType"
                    value="random"
                    checked={formData.addressType === 'random'}
                    onChange={handleChange}
                  />
                  随机地址
                </RadioLabel>
                <RadioLabel>
                  <RadioInput
                    type="radio"
                    name="addressType"
                    value="specified"
                    checked={formData.addressType === 'specified'}
                    onChange={handleChange}
                  />
                  指定地址
                </RadioLabel>
              </RadioGroup>
            </FormGroup>

            {formData.addressType === 'specified' && (
              <FormGroup>
                <Label htmlFor="inscriptionName">指定地址描述</Label>
                <Input
                  type="text"
                  id="specifiedDesc"
                  name="specifiedDesc"
                  value={formData.specifiedDesc}
                  onChange={handleChange}
                  placeholder="比如: 给ordi持仓前1000进行空投, 给金派持仓前1000进行空投"
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="orderAddress">
                {addressInputMethod === 'wallet' 
                  ? '下单地址(订单记录与此地址绑定, 地址为连接okx钱包地址)' 
                  : '下单地址(订单记录与此地址绑定, 请输入您的BTC地址)'}
              </Label>
              <AddressInput
                type="text"
                id="orderAddress"
                name="orderAddress"
                value={formData.orderAddress}
                onChange={handleChange}
                placeholder={addressInputMethod === 'wallet' 
                  ? "请先连接 OKX 钱包获取地址" 
                  : "请输入您的BTC地址"
                }
                disabled={addressInputMethod === 'wallet'}
              />
              {addressInputMethod === 'wallet' && (
                <AddressNote>
                  {formData.orderAddress
                    ? '已获取您的 BTC 地址，铭文将空投至此地址'
                    : '请连接 OKX 钱包以获取您的 BTC 地址'}
                </AddressNote>
              )}
              {addressInputMethod === 'manual' && (
                <AddressNote>
                  请确保输入的BTC地址正确，铭文将空投至此地址
                </AddressNote>
              )}
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <SubmitButton type="submit" disabled={loading}>
              {loading ? '处理中...' : '确认下单'}
            </SubmitButton>
          </FormContainer>
          <NavigationContainer>
            <StyledLink to="/history">查看历史空投记录</StyledLink>
          </NavigationContainer>
        </>
      ) : (
        <>
          <QRCodeDisplay
            btcAddress={paymentInfo.btcAddress}
            btcAmount={paymentInfo.btcAmount}
            inscriptionAddress={paymentInfo.inscriptionAddress}
            inscriptionAmount={paymentInfo.inscriptionAmount}
          />
          <NavigationContainer>
            <StyledLink to="/history">查看历史空投记录</StyledLink>
          </NavigationContainer>
        </>
      )}
      <DisclaimerContainer>
        <DisclaimerTitle>免责声明</DisclaimerTitle>
        <DisclaimerText>
          本服务仅供区块链技术研究和学习使用，不构成任何投资建议。用户使用本服务所产生的任何风险和后果由用户自行承担，平台不承担任何法律责任。铭文资产价值具有高度波动性，请用户自行评估风险。
        </DisclaimerText>
        <RegionRestriction>
          注意：本服务不面向中国大陆用户开放，中国大陆用户请勿使用本服务。用户须确保自己在使用本服务时遵守当地法律法规。
        </RegionRestriction>
      </DisclaimerContainer>
    </PageContainer>
  );
};

// 样式组件定义
const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px;
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: 40px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 36px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 28px;
    margin-bottom: 30px;
  }
`;

const FormContainer = styled.form`
  background-color: rgba(30, 30, 30, 0.6);
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 20px;
  }
`;

const FormTitle = styled.h2`
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 30px;
  font-size: 24px;
  font-weight: 500;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 15px;
  background-color: ${({ theme }) => theme.colors.inputBg};
  border: 1px solid #333;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.text};
  font-size: 16px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.secondary};
  }
  
  &::placeholder {
    color: #777;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const RadioInput = styled.input`
  margin-right: 8px;
  cursor: pointer;
  accent-color: ${({ theme }) => theme.colors.secondary};
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  background-color: ${({ theme }) => theme.colors.buttonBg};
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s, transform 0.2s;
  margin-top: 10px;
  
  &:hover:not(:disabled) {
    background-color: #e78c19;
    transform: translateY(-2px);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.errorColor};
  background-color: rgba(255, 85, 85, 0.1);
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 30px 0;
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.secondary};
  text-decoration: none;
  font-size: 16px;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  transition: all 0.3s;
  
  &:hover {
    background-color: rgba(247, 147, 26, 0.1);
  }
`;

const DisclaimerContainer = styled.div`
  background-color: rgba(20, 20, 20, 0.7);
  border-radius: 8px;
  padding: 20px;
  margin-top: 40px;
  border-left: 3px solid ${({ theme }) => theme.colors.secondary};
`;

const DisclaimerTitle = styled.h3`
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 10px;
  font-size: 18px;
`;

const DisclaimerText = styled.p`
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  margin-bottom: 15px;
  line-height: 1.6;
`;

const RegionRestriction = styled.p`
  color: #ff5555;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.6;
`;

const AddressInput = styled(Input)`
  background-color: ${({ theme, disabled }) =>
    disabled ? 'rgba(30, 30, 30, 0.8)' : theme.colors.inputBg};
  color: ${({ theme, value }) =>
    value ? theme.colors.secondary : '#555'};
  font-weight: ${({ value }) => value ? '500' : 'normal'};
`;

const AddressNote = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 5px;
`;

// 新增的样式组件，用于切换地址输入方式
const AddressMethodSelector = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  background-color: rgba(30, 30, 30, 0.6);
  border-radius: 8px;
  padding: 10px;
`;

const MethodButton = styled.button`
  padding: 10px 20px;
  background-color: ${({ active, theme }) => 
    active ? theme.colors.secondary : 'transparent'};
  color: ${({ active }) => active ? '#000' : '#fff'};
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 6px;
  font-size: 14px;
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: ${({ active, theme }) => 
      active ? theme.colors.secondary : 'rgba(247, 147, 26, 0.2)'};
  }
`;

export default AirdropOrderPage;